import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Loader from "../../components/loader";
import api from "../../core/axios";

interface PaymentCompletionGuardProps {
  children: React.ReactNode;
}

/**
 * PaymentCompletionGuard - Redirects users with incomplete payments to checkout
 *
 * This guard uses cached subscription status from the AuthContext bootstrap data,
 * eliminating the need for a separate API call on every page navigation.
 *
 * The subscription status is loaded once during auth initialization via the
 * /organizations/bootstrap endpoint and stored in AuthContext.currentOrgSubscription.
 */
const PaymentCompletionGuard: React.FC<PaymentCompletionGuardProps> = ({
  children,
}) => {
  const { organizations, currentOrgSubscription } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Track if we've already completed the check to prevent re-running
  const checkCompleted = useRef(false);
  const lastCheckedPath = useRef<string>("");

  // Skip guard on payment-related pages to prevent infinite loops
  const isPaymentPage =
    location.pathname.includes("/onboarding/payment-status") ||
    location.pathname.startsWith("/onboarding") ||
    location.pathname.startsWith("/accounts/");

  useEffect(() => {
    // Skip guard logic entirely on payment pages
    if (isPaymentPage) {
      setIsChecking(false);
      return;
    }

    // If we've already completed the check for this path and subscription hasn't changed, skip
    if (checkCompleted.current && lastCheckedPath.current === location.pathname) {
      return;
    }

    const checkSubscriptionStatus = async () => {
      // Skip check if no organizations (still loading)
      if (!organizations || organizations.length === 0) {
        // Don't mark as complete - still waiting for data
        setIsChecking(false);
        return;
      }

      // Skip if subscription data is still null (bootstrap still loading)
      if (currentOrgSubscription === null) {
        // Don't mark as complete - still waiting for bootstrap
        return;
      }

      // Mark that we're checking this path
      lastCheckedPath.current = location.pathname;

                                                                                                                                                                                                                                                                                                                                                                                                                                                                          ("📋 Checking subscription status from bootstrap:", {
        needsPayment: currentOrgSubscription.needsPayment,
        status: currentOrgSubscription.subscription?.status,
        planName: currentOrgSubscription.subscription?.planName,
      });

      if (currentOrgSubscription.needsPayment) {
        console.log("💳 Subscription incomplete, redirecting to payment...");
        setIsRedirecting(true);

        // Get current organization for checkout URL
        let currentOrganization: any = null;
        try {
          const currentOrgJson = localStorage.getItem("currentOrganization");
          if (currentOrgJson) {
            currentOrganization = JSON.parse(currentOrgJson);
          } else if (organizations.length > 0) {
            currentOrganization = organizations[0];
          }
        } catch (error) {
          console.error("Failed to parse currentOrganization:", error);
          if (organizations.length > 0) {
            currentOrganization = organizations[0];
          }
        }

        if (!currentOrganization) {
          console.error("No current organization found");
          setIsRedirecting(false);
          setIsChecking(false);
          checkCompleted.current = true;
          return;
        }

        // Use cached checkout URL if available, otherwise fetch
        if (currentOrgSubscription.checkoutUrl) {
          window.location.href = currentOrgSubscription.checkoutUrl;
        } else {
          // Fetch checkout URL (only API call needed when payment is actually incomplete)
          try {
            const checkoutResponse = await api.get(
              `/organizations/${currentOrganization.id}/checkout-url`,
            );
            const checkoutData =
              checkoutResponse.data.data || checkoutResponse.data;

            if (checkoutData.checkoutUrl) {
              window.location.href = checkoutData.checkoutUrl;
            } else {
              console.error("No checkout URL returned");
              setIsRedirecting(false);
              setIsChecking(false);
              checkCompleted.current = true;
            }
          } catch (error) {
            console.error("Failed to get checkout URL:", error);
            setIsRedirecting(false);
            setIsChecking(false);
            checkCompleted.current = true;
          }
        }
        return;
      }

      // Subscription is active/valid, allow access
      setIsChecking(false);
      checkCompleted.current = true;
    };

    checkSubscriptionStatus();
  }, [organizations, currentOrgSubscription, isPaymentPage, location.pathname]);

  // Reset check when navigating to a new page (but not on re-renders)
  useEffect(() => {
    // Only reset if we're navigating to a truly different path
    if (lastCheckedPath.current && lastCheckedPath.current !== location.pathname) {
      // For same-session navigation, we don't need to re-check subscription
      // unless the user explicitly refreshes bootstrap data
      // Just allow through if we've already verified subscription is valid
      if (checkCompleted.current && !isRedirecting) {
        setIsChecking(false);
      }
    }
  }, [location.pathname, isRedirecting]);

  if (isChecking || isRedirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader />
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default PaymentCompletionGuard;
